import Cookies from 'js-cookie';

/** Coordinate bounds for valid WGS84 latitude/longitude pairs. */
const MIN_LATITUDE = -90;
const MAX_LATITUDE = 90;
const MIN_LONGITUDE = -180;
const MAX_LONGITUDE = 180;

/**
 * Represents a named map location.
 * @typedef {Object} LocationRecord
 * @property {string} label
 * @property {string} city
 * @property {string} state
 * @property {string} stateLong
 * @property {number} latitude
 * @property {number} longitude
 * @property {string[]} [city_ids] All CW-confirmed city ids (varchars, e.g. ["KNSE"]); empty/absent when unconfirmed.
 */

/**
 * Represents location values read from cookies.
 * @typedef {Object} SavedLocationCookieData
 * @property {number | null} latitude
 * @property {number | null} longitude
 * @property {string} city
 * @property {string} state
 * @property {string[]} [city_ids] All CW-confirmed city ids, when present.
 */

/** @typedef {'saved' | 'default'} LocationSource */

/**
 * Result object returned after validating location cookies.
 * @typedef {Object} ValidatedLocationCookies
 * @property {LocationSource} source
 * @property {[number, number]} coordinates
 */

/** @type {Readonly<LocationRecord>} */
export const DEFAULT_LOCATION = Object.freeze({
  place_id: 'ChIJb32e8aVekIgRR5HId0l6lRU',
  DefaultLocationUserHome: true,
  defaultPlace: false,
  type: 'geocode',
  term: 'Jay',
  title: 'Jay',
  geometry: {
    location: {
      lat: 30.9529611,
      lng: -87.151359,
    },
    viewport: {
      northeast: {
        lat: 30.96039398314522,
        lng: -87.140743064385,
      },
      southwest: {
        lat: 30.93823797394665,
        lng: -87.16689096009338,
      },
    },
  },
  description: 'Jay, FL, USA',
  city: 'Jay',
  state_short: 'FL',
  state_long: 'Florida',
  place_data: {
    address_components: [
      {
        long_name: 'Jay',
        short_name: 'Jay',
        types: ['locality', 'political'],
      },
      {
        long_name: 'Santa Rosa County',
        short_name: 'Santa Rosa County',
        types: ['administrative_area_level_2', 'political'],
      },
      {
        long_name: 'Florida',
        short_name: 'FL',
        types: ['administrative_area_level_1', 'political'],
      },
      {
        long_name: 'United States',
        short_name: 'US',
        types: ['country', 'political'],
      },
      {
        long_name: '32565',
        short_name: '32565',
        types: ['postal_code'],
      },
    ],
    formatted_address: 'Jay, FL 32565, USA',
    geometry: {
      location: {
        lat: 30.9529611,
        lng: -87.151359,
      },
      viewport: {
        northeast: {
          lat: 30.96039398314522,
          lng: -87.140743064385,
        },
        southwest: {
          lat: 30.93823797394665,
          lng: -87.16689096009338,
        },
      },
    },
    name: 'Jay',
    types: ['locality', 'political'],
  },
  latitude: 30.9529611,
  longitude: -87.151359,
  city_ids: ['KNSE'],
});

/** @type {readonly [number, number]} */
export const DEFAULT_LOCATION_COORDS = Object.freeze([
  DEFAULT_LOCATION.latitude,
  DEFAULT_LOCATION.longitude,
]);

/** User-facing message shown when geolocation permissions are unavailable. */
export const LOCATION_PERMISSION_DISABLED_MESSAGE =
  "Location access is unavailable or disabled, please enable your browser's location permission for our site to utilize your current location instead.. Fusion is using the default location for maps and location-based widgets.";

/**
 * Checks if latitude/longitude values are finite and in valid bounds.
 * @param {number | null} latitude
 * @param {number | null} longitude
 * @returns {boolean}
 */
const hasValidCoordinateRange = (latitude, longitude) =>
  latitude !== null &&
  longitude !== null &&
  latitude >= MIN_LATITUDE &&
  latitude <= MAX_LATITUDE &&
  longitude >= MIN_LONGITUDE &&
  longitude <= MAX_LONGITUDE;

/**
 * Returns true when the pair points to Null Island (0, 0).
 * @param {number | null} latitude
 * @param {number | null} longitude
 * @returns {boolean}
 */
export const isZeroCoordinatePair = (latitude, longitude) => latitude === 0 && longitude === 0;

/**
 * Validates saved coordinates and rejects the (0, 0) pair.
 * @param {number | null} latitude
 * @param {number | null} longitude
 * @returns {boolean}
 */
export const hasValidSavedCoordinates = (latitude, longitude) =>
  hasValidCoordinateRange(latitude, longitude) && !isZeroCoordinatePair(latitude, longitude);
